/**
 * useUndoRedo Hook
 * 
 * Snapshot-based undo/redo system untuk material editing:
 * - Capture material snapshots sebelum changes
 * - Store up to 50 snapshots
 * - Undo/Redo dengan Ctrl+Z/Y
 * - Serial numbering untuk user-friendly history
 * - Debouncing untuk prevent spam snapshots
 * 
 * @returns {Object} Undo/redo state dan methods
 */

import { useRef, useState, useCallback } from 'react'
import { captureMaterialSnapshot, restoreMaterialSnapshot } from '@utils/materialSnapshot'

const MAX_SNAPSHOTS = 50

export function useUndoRedo() {
  const snapshotCacheRef = useRef([])
  const snapshotIndexRef = useRef(-1)
  const lastSnapshotTimeRef = useRef(0)

  const [snapshotTrigger, setSnapshotTrigger] = useState(0) // Trigger UI updates

  // Save snapshot with debouncing
  const saveSnapshot = useCallback(
    (material, description = 'Material Change', skipDebounce = false) => {
      if (!material) return

      const now = Date.now()

      // Debounce (except for color changes)
      if (!skipDebounce) {
        const timeSinceLastSave = now - lastSnapshotTimeRef.current
        if (timeSinceLastSave < 300) {
          console.log(`⚠️ Debounced - too soon! (${timeSinceLastSave}ms)`)
          return
        }
      }

      const snapshot = captureMaterialSnapshot(material)
      if (!snapshot) return

      // Remove future snapshots
      const currentIndex = snapshotIndexRef.current
      const newCache = snapshotCacheRef.current.slice(0, currentIndex + 1)

      // Add serial number
      const serialNumber = newCache.length + 1
      snapshot._serial = serialNumber
      snapshot.description = description

      newCache.push(snapshot)

      // Limit to MAX_SNAPSHOTS
      if (newCache.length > MAX_SNAPSHOTS) {
        newCache.shift()
        newCache.forEach((s, idx) => {
          s._serial = idx + 1
        })
      } else {
        snapshotIndexRef.current++
      }

      snapshotCacheRef.current = newCache
      lastSnapshotTimeRef.current = now

      console.log(`💾 Snapshot #${snapshot._serial} saved!`)
      setSnapshotTrigger((prev) => prev + 1)
    },
    []
  )

  // Undo
  const undo = useCallback(() => {
    if (snapshotIndexRef.current < 0) return false

    const snapshot = snapshotCacheRef.current[snapshotIndexRef.current]

    if (!snapshot || !snapshot._materialRef) {
      console.error('❌ Undo failed: invalid snapshot')
      return false
    }

    const restored = restoreMaterialSnapshot(snapshot)
    if (!restored) {
      console.error('❌ Undo failed: could not restore material')
      return false
    }

    snapshotIndexRef.current--
    setSnapshotTrigger((prev) => prev + 1)
    console.log('✅ Undo successful, index now:', snapshotIndexRef.current)
    return true
  }, [])

  // Redo
  const redo = useCallback(() => {
    if (snapshotIndexRef.current >= snapshotCacheRef.current.length - 1) {
      return false
    }

    snapshotIndexRef.current++
    const snapshot = snapshotCacheRef.current[snapshotIndexRef.current]

    if (!snapshot || !snapshot._materialRef) {
      console.error('❌ Redo failed: invalid snapshot')
      snapshotIndexRef.current--
      return false
    }

    const restored = restoreMaterialSnapshot(snapshot)
    if (!restored) {
      console.error('❌ Redo failed: could not restore material')
      snapshotIndexRef.current--
      return false
    }

    setSnapshotTrigger((prev) => prev + 1)
    console.log('✅ Redo successful, index now:', snapshotIndexRef.current)
    return true
  }, [])

  // Clear history
  const clearHistory = useCallback(() => {
    snapshotCacheRef.current = []
    snapshotIndexRef.current = -1
    setSnapshotTrigger((prev) => prev + 1)
  }, [])

  // Jump to specific snapshot
  const jumpToSnapshot = useCallback((index) => {
    if (index < 0 || index >= snapshotCacheRef.current.length) return false

    const snapshot = snapshotCacheRef.current[index]
    if (!snapshot || !restoreMaterialSnapshot(snapshot)) return false

    snapshotIndexRef.current = index
    setSnapshotTrigger((prev) => prev + 1)
    console.log(`✅ Jumped to snapshot #${snapshot._serial}`)
    return true
  }, [])

  return {
    snapshotCacheRef,
    snapshotIndexRef,
    snapshotTrigger,
    canUndo: snapshotIndexRef.current >= 0,
    canRedo: snapshotIndexRef.current < snapshotCacheRef.current.length - 1,
    
    saveSnapshot,
    undo,
    redo,
    clearHistory,
    jumpToSnapshot,
  }
}

